/**
 * AdvertiserNotificationsPage — manage notification subscriptions and
 * view the recent event feed for an advertiser.
 *
 * Sections:
 *   1. Advertiser picker (free-form ID input, persisted across tabs).
 *   2. Subscriptions card — list + create (channel, target, event types) + delete.
 *   3. Event feed card — list recent events with kind/status badges + mark-read.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useSubscriptionsList,
	useFeedList,
	useCreateSubscription,
	useDeleteSubscription,
	useUpdateSubscription,
	useMarkEventRead,
} from "./hooks";
import type {
	NotificationChannel,
	NotificationEventKind,
	NotificationEventStatus,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const SELECT = INPUT;
const BTN =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const CHANNELS: readonly NotificationChannel[] = ["email", "webhook", "slack"];
const KINDS: readonly NotificationEventKind[] = [
	"conversion",
	"approval",
	"invoice",
];

function statusTone(status: NotificationEventStatus): string {
	switch (status) {
		case "delivered":
			return "border-green-200 bg-green-50 text-green-700";
		case "read":
			return "border-sky-200 bg-sky-50 text-sky-700";
		case "failed":
			return "border-red-200 bg-red-50 text-red-700";
		default:
			return "border-zinc-200 bg-zinc-50 text-zinc-600";
	}
}

function kindTone(kind: NotificationEventKind): string {
	switch (kind) {
		case "conversion":
			return "border-green-200 bg-green-50 text-green-700";
		case "approval":
			return "border-sky-200 bg-sky-50 text-sky-700";
		case "invoice":
			return "border-zinc-200 bg-zinc-50 text-zinc-700";
	}
}

function fmtTime(epochSec: number | null | undefined): string {
	if (!epochSec) return "-";
	try {
		return new Date(epochSec * 1000).toISOString().replace("T", " ").slice(0, 19);
	} catch {
		return String(epochSec);
	}
}

export function AdvertiserNotificationsPage() {
	const [advertiserId, setAdvertiserId] = useState("");
	const [activeAdvertiser, setActiveAdvertiser] = useState("");

	// form state
	const [channel, setChannel] = useState<NotificationChannel>("email");
	const [target, setTarget] = useState("");
	const [eventTypes, setEventTypes] = useState<NotificationEventKind[]>([
		"conversion",
	]);

	const subsQuery = useSubscriptionsList(activeAdvertiser, activeAdvertiser.length > 0);
	const feedQuery = useFeedList(activeAdvertiser, 50, activeAdvertiser.length > 0);
	const createMut = useCreateSubscription();
	const updateMut = useUpdateSubscription();
	const deleteMut = useDeleteSubscription();
	const markReadMut = useMarkEventRead();

	const subs = subsQuery.data?.subscriptions ?? [];
	const events = feedQuery.data?.events ?? [];

	const subscriptionsEmpty = activeAdvertiser.length > 0 && subs.length === 0;
	const eventsEmpty = activeAdvertiser.length > 0 && events.length === 0;

	const canSubmit = useMemo(
		() =>
			activeAdvertiser.length > 0 &&
			target.trim().length > 0 &&
			eventTypes.length > 0 &&
			!createMut.isPending,
		[activeAdvertiser, target, eventTypes.length, createMut.isPending],
	);

	const handleAdvertiserSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next = advertiserId.trim();
		if (next.length === 0) return;
		setActiveAdvertiser(next);
	};

	const handleCreateSub = (e: FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;
		createMut.mutate(
			{
				advertiser_id: activeAdvertiser,
				channel,
				target: target.trim(),
				event_types: eventTypes,
				enabled: true,
			},
			{
				onSuccess: () => {
					setTarget("");
				},
			},
		);
	};

	const toggleEventType = (k: NotificationEventKind) => {
		setEventTypes((prev) =>
			prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k],
		);
	};

	return (
		<main
			data-testid="advertiser-notifications-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-notifications
				</p>
				<h1 className="bg-gradient-to-r from-green-700 to-green-500 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser notifications
				</h1>
				<p className="text-sm text-zinc-600">
					Instant alerts when conversions fire. Configure channels, watch the feed.
				</p>
			</header>

			<section className={CARD} aria-label="Advertiser picker">
				<h2 className="text-base font-semibold text-zinc-900">Advertiser</h2>
				<form
					onSubmit={handleAdvertiserSubmit}
					className="mt-3 flex flex-col gap-3 sm:flex-row"
					data-testid="advertiser-notifications-advertiser-form"
				>
					<input
						type="text"
						className={INPUT}
						placeholder="advertiser_id (e.g. adv_123)"
						value={advertiserId}
						onChange={(e) => setAdvertiserId(e.currentTarget.value)}
						data-testid="advertiser-notifications-advertiser-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={advertiserId.trim().length === 0}
						data-testid="advertiser-notifications-advertiser-submit"
					>
						Load
					</button>
				</form>
				{activeAdvertiser ? (
					<p className="mt-2 text-xs text-zinc-500">
						Viewing:{" "}
						<span className="font-mono text-zinc-700">{activeAdvertiser}</span>
					</p>
				) : (
					<p className="mt-2 text-xs text-zinc-500">
						Enter an advertiser ID to load subscriptions and event feed.
					</p>
				)}
			</section>

			<section className={CARD} aria-label="Subscriptions">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Subscriptions</h2>
					{subsQuery.isFetching && activeAdvertiser ? (
						<span className="text-xs text-zinc-500">Refreshing.</span>
					) : null}
				</div>

				<form
					onSubmit={handleCreateSub}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[140px_1fr_auto]"
					data-testid="advertiser-notifications-sub-form"
				>
					<select
						className={SELECT}
						value={channel}
						onChange={(e) =>
							setChannel(e.currentTarget.value as NotificationChannel)
						}
						disabled={!activeAdvertiser || createMut.isPending}
						data-testid="advertiser-notifications-channel-select"
					>
						{CHANNELS.map((c) => (
							<option key={c} value={c}>
								{c}
							</option>
						))}
					</select>
					<input
						type="text"
						className={INPUT}
						placeholder="target (email address, webhook URL, slack channel)"
						value={target}
						onChange={(e) => setTarget(e.currentTarget.value)}
						disabled={!activeAdvertiser || createMut.isPending}
						data-testid="advertiser-notifications-target-input"
					/>
					<button
						type="submit"
						className={BTN}
						disabled={!canSubmit}
						data-testid="advertiser-notifications-sub-submit"
					>
						{createMut.isPending ? "Saving." : "Subscribe"}
					</button>
				</form>

				<div className="mt-3 flex flex-wrap gap-2" data-testid="advertiser-notifications-kinds">
					{KINDS.map((k) => {
						const on = eventTypes.includes(k);
						return (
							<button
								key={k}
								type="button"
								onClick={() => toggleEventType(k)}
								className={
									on
										? "rounded-[6px] border border-green-600 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700"
										: "rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
								}
								data-testid={`advertiser-notifications-kind-${k}`}
							>
								{k}
							</button>
						);
					})}
				</div>

				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}

				<div className="mt-4">
					{!activeAdvertiser ? (
						<p className="text-sm text-zinc-500">
							Select an advertiser to see subscriptions.
						</p>
					) : subsQuery.isLoading ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="advertiser-notifications-subs-loading"
						>
							Loading.
						</p>
					) : subsQuery.error ? (
						<p className="text-sm text-red-600" role="alert">
							{subsQuery.error.message}
						</p>
					) : subscriptionsEmpty ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="advertiser-notifications-subs-empty"
						>
							No subscriptions yet.
						</p>
					) : (
						<ul
							className="divide-y divide-zinc-200"
							data-testid="advertiser-notifications-subs-list"
						>
							{subs.map((s) => (
								<li
									key={s.id}
									className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between"
									data-testid={`advertiser-notifications-sub-${s.id}`}
								>
									<div className="flex flex-col">
										<div className="flex items-center gap-2">
											<span className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-600">
												{s.channel}
											</span>
											<span className="font-mono text-xs text-zinc-700">
												{s.target}
											</span>
										</div>
										<p className="mt-0.5 text-[11px] text-zinc-500">
											{s.event_types} | created {fmtTime(s.created_at)}
										</p>
									</div>
									<div className="flex items-center gap-2">
										<button
											type="button"
											className={BTN_GHOST}
											disabled={updateMut.isPending}
											onClick={() =>
												updateMut.mutate({
													id: s.id,
													patch: { enabled: s.enabled === 1 ? false : true },
												})
											}
											data-testid={`advertiser-notifications-sub-toggle-${s.id}`}
										>
											{s.enabled === 1 ? "Disable" : "Enable"}
										</button>
										<button
											type="button"
											className={BTN_GHOST}
											disabled={deleteMut.isPending}
											onClick={() => deleteMut.mutate(s.id)}
											data-testid={`advertiser-notifications-sub-delete-${s.id}`}
										>
											Delete
										</button>
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			</section>

			<section className={CARD} aria-label="Event feed">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Event feed</h2>
					{feedQuery.isFetching && activeAdvertiser ? (
						<span className="text-xs text-zinc-500">Refreshing.</span>
					) : null}
				</div>
				<div className="mt-3">
					{!activeAdvertiser ? (
						<p className="text-sm text-zinc-500">
							Select an advertiser to see the feed.
						</p>
					) : feedQuery.isLoading ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="advertiser-notifications-feed-loading"
						>
							Loading.
						</p>
					) : feedQuery.error ? (
						<p className="text-sm text-red-600" role="alert">
							{feedQuery.error.message}
						</p>
					) : eventsEmpty ? (
						<p
							className="text-sm text-zinc-500"
							data-testid="advertiser-notifications-feed-empty"
						>
							No events yet.
						</p>
					) : (
						<ul
							className="divide-y divide-zinc-200"
							data-testid="advertiser-notifications-feed-list"
						>
							{events.map((ev) => (
								<li
									key={ev.id}
									className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:justify-between"
									data-testid={`advertiser-notifications-event-${ev.id}`}
								>
									<div className="flex min-w-0 flex-col">
										<div className="flex flex-wrap items-center gap-2">
											<span
												className={`rounded-[6px] border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${kindTone(
													ev.kind,
												)}`}
											>
												{ev.kind}
											</span>
											<span
												className={`rounded-[6px] border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${statusTone(
													ev.status,
												)}`}
											>
												{ev.status}
											</span>
											<span className="text-[11px] text-zinc-500">
												{fmtTime(ev.created_at)}
											</span>
										</div>
										<p className="mt-1 max-w-full truncate font-mono text-[11px] text-zinc-600">
											{ev.payload}
										</p>
									</div>
									<div className="flex items-center gap-2">
										{ev.status !== "read" ? (
											<button
												type="button"
												className={BTN_GHOST}
												disabled={markReadMut.isPending}
												onClick={() => markReadMut.mutate(ev.id)}
												data-testid={`advertiser-notifications-event-mark-read-${ev.id}`}
											>
												Mark read
											</button>
										) : null}
									</div>
								</li>
							))}
						</ul>
					)}
				</div>
			</section>
		</main>
	);
}

export default AdvertiserNotificationsPage;
